import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Chip,
    TextField,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Mic as MicIcon,
    Stop as StopIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import useDictation from '../../../hooks/useDictation';

// Три категории посуточных заметок. Ключ совпадает с полем в day_narratives JSON.
const CATEGORIES = [
    { key: 'workPerformance', label: 'Work Performance', color: '#16a34a' },
    { key: 'delays',          label: 'Delays',           color: '#d97706' },
    { key: 'extraWork',       label: 'Extra Work',       color: '#7c3aed' },
];

function weekdayShort(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}
function shortDate(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Секция посуточных заметок на этапе подписи: Work Performance / Delays / Extra Work.
 * Для каждой категории — ряд чипов дней недели; тап по дню открывает поле с
 * надиктовкой. Заполненные дни помечены. Полный CRUD: add (тап пустого дня),
 * edit (правка текста), delete (крестик). Всё видно сразу.
 *
 * Контролируемая: value = { workPerformance:{date:text}, delays:{...}, extraWork:{...} }.
 *
 * Props:
 *   - weekDates: string[]  — 7 ISO-дат недели (Mon..Sun)
 *   - value: object        — текущее состояние заметок
 *   - onChange: (next) => void
 */
export default function SignDayNarrativesSection({ weekDates, value, onChange }) {
    // Открытые (для редактирования) дни по категориям — включая пустые, которые
    // пользователь только что тапнул. Заполненные дни показываются всегда.
    const [openDays, setOpenDays] = useState({}); // { [catKey]: Set<date> }
    const [dictKey, setDictKey] = useState(null);

    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation,
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });

    const textOf = (cat, date) => (value?.[cat]?.[date]) || '';
    const isFilled = (cat, date) => !!textOf(cat, date).trim();

    const setDay = useCallback((cat, date, text) => {
        const next = { ...(value || {}) };
        const catMap = { ...(next[cat] || {}) };
        if (text && text.trim()) catMap[date] = text;
        else delete catMap[date];
        if (Object.keys(catMap).length) next[cat] = catMap;
        else delete next[cat];
        onChange(next);
    }, [value, onChange]);

    const toggleOpen = (cat, date) => {
        setOpenDays(prev => {
            const set = new Set(prev[cat] || []);
            if (set.has(date)) set.delete(date);
            else set.add(date);
            return { ...prev, [cat]: set };
        });
    };

    const closeDay = (cat, date) => {
        // Удаление записи дня: чистим текст и закрываем редактор.
        setDay(cat, date, '');
        setOpenDays(prev => {
            const set = new Set(prev[cat] || []);
            set.delete(date);
            return { ...prev, [cat]: set };
        });
    };

    const runDictation = async (cat, date) => {
        if (!dictationSupported) return;
        const key = `${cat}:${date}`;
        if (isListening && dictKey === key) { stopDictation(); return; }
        if (isListening) stopDictation();
        setDictKey(key);
        await startDictation({
            onResult: (transcript) => {
                const cur = textOf(cat, date);
                const space = cur && !cur.endsWith(' ') ? ' ' : '';
                setDay(cat, date, cur + space + transcript);
            },
            onEnd: () => setDictKey(null),
            onError: () => setDictKey(null),
        });
    };

    return (
        <Box>
            {CATEGORIES.map(cat => {
                const filledDates = weekDates.filter(d => isFilled(cat.key, d));
                const openSet = openDays[cat.key] || new Set();
                // Видимые редакторы: заполненные + только что открытые пустые, в порядке недели.
                const visibleDates = weekDates.filter(d => isFilled(cat.key, d) || openSet.has(d));

                return (
                    <Box
                        key={cat.key}
                        sx={{
                            borderLeft: `3px solid ${cat.color}`,
                            borderTop: '1px solid #eef2f7',
                            pl: 1, py: 0.5, mb: 0.25,
                        }}
                    >
                        {/* Метка категории + чипы дней на одной строке — компактно */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 118, flexShrink: 0 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: cat.color, lineHeight: 1.1 }}>
                                    {cat.label}
                                </Typography>
                                {filledDates.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={filledDates.length}
                                        sx={{ height: 15, fontSize: 10, fontWeight: 700, bgcolor: cat.color, color: '#fff', '& .MuiChip-label': { px: 0.6 } }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, flex: 1, minWidth: 0 }}>
                                {weekDates.map(d => {
                                    const filled = isFilled(cat.key, d);
                                    const open = openSet.has(d);
                                    return (
                                        <Box
                                            key={d}
                                            onClick={() => toggleOpen(cat.key, d)}
                                            sx={{
                                                cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent',
                                                px: 0.75, py: 0.25, minWidth: 36, textAlign: 'center',
                                                borderRadius: 1, border: '1px solid',
                                                borderColor: filled || open ? cat.color : '#cbd5e1',
                                                bgcolor: filled ? cat.color : (open ? `${cat.color}18` : '#fff'),
                                                color: filled ? '#fff' : 'text.primary', lineHeight: 1.05,
                                            }}
                                        >
                                            <Box sx={{ fontSize: 10.5, fontWeight: 700 }}>{weekdayShort(d)}</Box>
                                            <Box sx={{ fontSize: 8.5, opacity: filled ? 0.9 : 0.6 }}>{shortDate(d)}</Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Editors for visible days */}
                        {visibleDates.map(d => {
                            const key = `${cat.key}:${d}`;
                            const listeningHere = isListening && dictKey === key;
                            return (
                                <Box key={d} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.5, mb: 0.25 }}>
                                    <Chip
                                        size="small"
                                        label={`${weekdayShort(d)} ${shortDate(d)}`}
                                        sx={{ height: 24, fontSize: 11, fontWeight: 600, mt: 0.5, minWidth: 62 }}
                                    />
                                    <Box sx={{ position: 'relative', flex: 1 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={1}
                                            maxRows={4}
                                            size="small"
                                            placeholder={`${cat.label} for ${weekdayShort(d)} — tap mic to dictate…`}
                                            value={textOf(cat.key, d)}
                                            onChange={(e) => setDay(cat.key, d, e.target.value)}
                                            InputProps={{ sx: { pr: 5 } }}
                                        />
                                        {dictationSupported && (
                                            <Tooltip title={listeningHere ? 'Stop dictation' : 'Dictate'}>
                                                <IconButton
                                                    onClick={() => runDictation(cat.key, d)}
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute', right: 4, top: 4,
                                                        bgcolor: listeningHere ? 'error.main' : 'primary.main',
                                                        color: '#fff',
                                                        '&:hover': { bgcolor: listeningHere ? 'error.dark' : 'primary.dark' },
                                                    }}
                                                >
                                                    {listeningHere ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Tooltip title="Remove">
                                        <IconButton
                                            onClick={() => closeDay(cat.key, d)}
                                            size="small"
                                            sx={{ mt: 0.5, color: 'text.secondary' }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}
        </Box>
    );
}
